'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { type MarketImageDraft, MarketImagePicker } from '@/components/images/market-picker';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { SilkSheet } from '@/components/ui/silk-sheet';
import type { ImageGenerationMode } from '@/events/image-generation';
export interface MarketImagePickerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: ImageGenerationMode;
  marketTitle: string;
  /** Required when mode === 'outcome'. Ignored for 'cover'. */
  outcomeLabel?: string | undefined;
  /** Receives the cropped local File draft and closes the modal immediately. */
  onPending: (draft: MarketImageDraft) => void;
  onReject: (error: Error) => void;
}

/**
 * Thin dialog wrapper around `<MarketImagePicker>`. Used for the outcome
 * thumbnail trigger in `<OutcomeInput>` — those rows are too tight to host
 * the tabs inline, so a second dialog on top of the wizard is unavoidable.
 *
 * The cover step in the create-proposal wizard renders `<MarketImagePicker>`
 * directly inline (no modal wrapping) — see `create-proposal-step-cover.tsx`.
 */
export function MarketImagePickerModal(t0) {
  const $ = _c(25);
  const {
    open,
    onOpenChange,
    mode,
    marketTitle,
    outcomeLabel,
    onPending,
    onReject
  } = t0;
  const t = useTranslations("launchpad.imageGen");
  let t1;
  if ($[0] !== mode || $[1] !== t) {
    t1 = mode === "cover" ? t("title.cover") : t("title.outcome");
    $[0] = mode;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const dialogTitle = t1;
  let t2;
  if ($[3] !== onOpenChange || $[4] !== onPending) {
    t2 = function handlePending(draft) {
      onPending(draft);
      onOpenChange(false);
    };
    $[3] = onOpenChange;
    $[4] = onPending;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handlePending = t2;
  let t3;
  if ($[6] !== onOpenChange) {
    t3 = function handleCancel() {
      onOpenChange(false);
    };
    $[6] = onOpenChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleCancel = t3;
  let t4;
  if ($[8] !== mode) {
    t4 = [mode];
    $[8] = mode;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== handleCancel || $[11] !== handlePending || $[12] !== marketTitle || $[13] !== mode || $[14] !== onReject || $[15] !== outcomeLabel) {
    t5 = <MarketImagePicker mode={mode} marketTitle={marketTitle} outcomeLabel={outcomeLabel} onPending={handlePending} onReject={onReject} onCancel={handleCancel} showCancel={true} />;
    $[10] = handleCancel;
    $[11] = handlePending;
    $[12] = marketTitle;
    $[13] = mode;
    $[14] = onReject;
    $[15] = outcomeLabel;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <MotionAnimatedAutoHeight layoutDependencies={t4}>{t5}</MotionAnimatedAutoHeight>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== dialogTitle || $[21] !== onOpenChange || $[22] !== open || $[23] !== t6) {
    t7 = <SilkSheet open={open} onOpenChange={onOpenChange} variant="detached" keyboard={true} title={dialogTitle} size="default">{t6}</SilkSheet>;
    $[20] = dialogTitle;
    $[21] = onOpenChange;
    $[22] = open;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  return t7;
}